/**
 * A bakery's working hours for ONE morning, derived once (PRD BAK-027).
 *
 * A location stores its pickup span per weekday, and every surface that wants
 * to state "this shop works 07:00 to 13:00" has to answer the same two
 * questions first: which weekday does this service day fall on, and is the shop
 * shut that day. Both were already answered privately inside the bakery route
 * (`weekdayOfIsoDay`), and the guest shop chooser is the second caller - which
 * is precisely the point at which this codebase has historically grown a second
 * answer instead of a shared one.
 *
 * So the questions are asked here, over the wire vocabulary (a `YYYY-MM-DD`
 * service day, `HH:MM` local times), pure and I/O-free like everything else in
 * this directory, and both the worker and the guest storefront import them.
 *
 * The weekday index is the `Date#getDay` one (0 = Sunday), which is what the
 * `earliest_ready` / `latest_pickup` tables are keyed by. It is the calendar
 * question rather than the pickup one, so it and the type that indexes it live
 * in `service-day.ts` beside the rest of the service day's arithmetic.
 */

import type { PickupHours } from '../models';
import { type ServiceDayWeekday, weekdayOfServiceDay } from './service-day';

/** One service: the span of a single opening (BAK-046). */
export interface PickupServiceSpan {
  readonly earliest: string;
  readonly latest: string | null;
}

/** A weekday's services, in order. An empty list is a day the shop is shut. */
export type PickupServicesByWeekday = Readonly<
  Record<ServiceDayWeekday, readonly PickupServiceSpan[]>
>;

/**
 * The shop's pickup hours on that morning, or null when it is shut.
 *
 * A weekday holds a LIST of services now (BAK-046), and what this answers is
 * their ENVELOPE - the first service's opening to the last one's close.
 *
 * That is not a new approximation, it is the one this field already carries:
 * `pickupHoursSpan` below has always merged several COUNTERS of one house into
 * a single span, so a house with a shop open 07:00-09:00 and another open
 * 17:00-19:00 has always advertised "07:00-19:00". The field is an envelope by
 * construction and its docstring says so. What a guest can actually book is
 * decided by the picker, and the picker is span-accurate: it will not offer a
 * pickup that falls in the gap.
 */
export function pickupHoursOn(
  services: PickupServicesByWeekday,
  serviceDay: string,
): PickupHours | null {
  return envelopeOf(services[weekdayOfServiceDay(serviceDay)]);
}

/** One weekday's services as one span: first opening to last close. */
function envelopeOf(day: readonly PickupServiceSpan[]): PickupHours | null {
  const first = day[0];
  if (first === undefined) return null;
  return { start: first.earliest, end: day[day.length - 1]?.latest ?? null };
}

/**
 * The span as one label: `07:00-13:00`, or just `07:00` when the day has no
 * upper bound. Times are already wall-clock in the shop's own zone, so there is
 * nothing here for `Intl` to convert - and converting them to the reader's zone
 * is the bug this avoids, not a feature it is missing.
 */
export function pickupHoursLabel(hours: PickupHours): string {
  return hours.end === null ? hours.start : `${hours.start}-${hours.end}`;
}

/**
 * The HOUSE'S span for one morning: the earliest start to the latest end
 * across its counters' individual spans (GST-030, finding F-33).
 *
 * A mode B entry is a BRAND, and a brand is many counters; the picker behind
 * the entry offers the union of what all of them can serve, so the hours the
 * entry states must cover that union - stating one counter's day beside a
 * picker that spans the house is how a card came to say "opens 09:00" one tap
 * before an 08:00 window. Counters shut that morning contribute nothing; a
 * counter with no upper bound (`end: null`) leaves the house unbounded too.
 * Null when every counter is shut - the same answer `pickupHoursOn` gives for
 * one closed counter.
 *
 * `HH:MM` is zero-padded, so lexical comparison IS chronological comparison.
 */
export function pickupHoursSpan(spans: readonly (PickupHours | null)[]): PickupHours | null {
  let folded: PickupHours | null = null;
  for (const span of spans) {
    if (span === null) continue;
    if (folded === null) {
      folded = span;
      continue;
    }
    folded = {
      start: span.start < folded.start ? span.start : folded.start,
      end:
        folded.end === null || span.end === null
          ? null
          : span.end > folded.end
            ? span.end
            : folded.end,
    };
  }
  return folded;
}

const WEEKDAYS_SUNDAY_FIRST: readonly ServiceDayWeekday[] = [0, 1, 2, 3, 4, 5, 6];

/**
 * A house's week: one entry per weekday, `Date#getDay` order (0 = Sunday), null
 * on a day it is shut. The guest shop chooser states it under the house's name.
 */
export type WeeklyPickupHours = readonly (PickupHours | null)[];

/**
 * The HOUSE'S week - `pickupHoursSpan` asked seven times, once per weekday, over
 * every counter of the house that can serve the address.
 *
 * The same envelope the single-morning field has always been (see
 * `pickupHoursOn`), so the week printed on a tile and the span the picker
 * unions cannot be two rules: a house is as open on a Tuesday as its widest
 * counter is on a Tuesday.
 */
export function weeklyPickupHours(counters: readonly PickupServicesByWeekday[]): WeeklyPickupHours {
  return WEEKDAYS_SUNDAY_FIRST.map((weekday) =>
    pickupHoursSpan(counters.map((services) => envelopeOf(services[weekday]))),
  );
}

/** The week as Paris reads it: Monday first, Sunday last. */
const WEEKDAYS_MONDAY_FIRST: readonly ServiceDayWeekday[] = [1, 2, 3, 4, 5, 6, 0];

/** A run of consecutive weekdays keeping the same hours; `from === to` is one day. */
export interface WeeklyHoursGroup {
  readonly from: ServiceDayWeekday;
  readonly to: ServiceDayWeekday;
  /** Null is a run of days the house is shut. */
  readonly hours: PickupHours | null;
}

/**
 * The week folded for reading: Monday first, consecutive days with identical
 * hours merged into one run. `Mon-Sat 07:00-20:00, Sun 08:00-13:00` is two
 * groups; a house keeping one span all week is ONE group running Monday to
 * Sunday, which is what lets a surface say "every day" instead of naming seven.
 *
 * Words are the surface's business, not this function's: it names weekdays by
 * index, so the French and the English tile are one derivation and two
 * catalogues. A malformed week (not seven entries) folds to nothing rather
 * than to a guess.
 */
export function weeklyHoursGroups(week: WeeklyPickupHours): readonly WeeklyHoursGroup[] {
  if (week.length !== 7) return [];
  const groups: WeeklyHoursGroup[] = [];
  for (const weekday of WEEKDAYS_MONDAY_FIRST) {
    const hours = week[weekday] ?? null;
    const last = groups[groups.length - 1];
    if (last !== undefined && sameHours(last.hours, hours)) {
      groups[groups.length - 1] = { ...last, to: weekday };
    } else {
      groups.push({ from: weekday, to: weekday, hours });
    }
  }
  return groups;
}

function sameHours(a: PickupHours | null, b: PickupHours | null): boolean {
  if (a === null || b === null) return a === b;
  return a.start === b.start && a.end === b.end;
}

/**
 * The HOUSE'S services on one day, as the house configured them: every
 * counter's services for that weekday, merged where they overlap or touch,
 * in order. `07:00-13:00` and `16:00-20:00` stay two; two counters keeping
 * `07:00-13:00` and `08:00-20:00` are one, `07:00-20:00`.
 *
 * This is what a tile states as when the house is open, and its `end` is the
 * CLOSING TIME - the figure the baker typed, never a delivery window's end.
 * The first version of the tile's last line folded the picker's windows
 * instead and printed "until 20:45" for a house that closes at 20:00, because
 * the last courier arrival runs past the counter's close (founder, 2026-09-18:
 * "just put the closing time").
 *
 * `after` drops services already over - today's tile does not advertise a
 * morning that ended at 13:00 when it is 15:00. A service with no upper bound
 * (`latest: null`) swallows everything after its start, as `pickupHoursSpan`
 * does. `HH:MM` is zero-padded, so lexical comparison is chronological.
 */
export function pickupServicesOn(
  counters: readonly PickupServicesByWeekday[],
  serviceDay: string,
  after: string | null = null,
): readonly PickupHours[] {
  const weekday = weekdayOfServiceDay(serviceDay);
  const services = counters
    .flatMap((counter) => counter[weekday])
    .map((service) => ({ start: service.earliest, end: service.latest }))
    .filter((service) => after === null || service.end === null || service.end > after)
    .sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : 0));

  const merged: { start: string; end: string | null }[] = [];
  for (const service of services) {
    const last = merged[merged.length - 1];
    if (last !== undefined && (last.end === null || service.start <= last.end)) {
      if (last.end !== null && (service.end === null || service.end > last.end)) {
        last.end = service.end;
      }
    } else {
      merged.push({ ...service });
    }
  }
  return merged;
}
